/**
 * =============================================================================
 * STOREFRONT WIDGET - ENTRY POINT
 * =============================================================================
 *
 * Main entry point for the storefront widget.
 * This script is injected into BigCommerce storefronts via the Scripts API.
 *
 * ARCHITECTURE REFERENCE: Section 9 - Storefront Widget
 * IMPLEMENTATION REFERENCE: PHASE_0B_SPEC.md §2.5.6
 *
 * Routes:
 * - /find-my-part/ask — QuestionForm for submitting new questions
 * - /find-my-part/my-questions — MyQuestions for viewing submitted questions
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback, useRef, Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { createRoot, type Root } from 'react-dom/client';
// Mount-point IDs are imported from @find-my-part/types so the renderer
// (worker/PublicRendererService) and this bootstrap cannot drift. The
// 2026-05-08 OGS sandbox Item #7 hydration failure was traced in part to
// the renderer emitting `<div id="fmp-widget-mount">` while the widget
// bootstrap only looked for `find-my-part-app`. SurfaceMountV1 now keeps
// both ids in one fail-closed distinct-element candidate grammar.
import { DEV_MOUNT_ID } from '@find-my-part/types';
import {
  NOOP_MOUNT_DECISION_EMITTER_V1,
  resolveSurfaceMountV1,
  type ResolvedMountPoint,
} from './mountContract';
import { installHubLayoutV1, type HubLayoutV1ResolutionContext } from './hubLayout';
import {
  installQuestionActionsIslandV1,
  resetQuestionActionsIslandV1,
} from './questionActionsIsland';
import { logger } from './lib/logger';
import { QuestionForm } from './components/QuestionForm';
import { MyQuestions } from './components/MyQuestions';
import { BrowseQuestionsView } from './views/BrowseQuestionsView';
import { QuestionDetailView } from './views/QuestionDetailView';
import { LaunchUnavailable } from './components/LaunchUnavailable';
import { widgetApi } from './api/client';
import { invalidatePublicReads } from './api/publicQuestionCache';
import { useAnnounce } from './hooks';
import { ViewerProvider } from './viewer';
import {
  BREAKPOINTS,
  SURFACE_MOUNT_V1_HUB_RESERVATION_RELEASED_CLASS,
  WIDGET_TOKEN_CSS,
  applyThemeTransaction,
  installWidgetTokenScope,
  readPublicThemeOverrides,
  subscribeToSchemePreference,
} from './tokens';

// =============================================================================
// CONSTANTS
// =============================================================================

const PAGE_PATH = '/find-my-part/';
let widgetInstanceSequence = 0;

/**
 * Widget routes.
 * These paths are relative to the PAGE_PATH.
 */
const ROUTES = {
  ASK: 'ask',
  MY_QUESTIONS: 'my-questions',
  BROWSE: 'browse',
  QUESTION_DETAIL: 'question',
} as const;

/** Shared JavaScript motion policy for skip-link and pagination scrolling. */
export function motionSafeScrollBehavior(
  matchMedia: Pick<Window, 'matchMedia'>['matchMedia'] = window.matchMedia.bind(window)
): ScrollBehavior {
  return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
}

type RouteKey = keyof typeof ROUTES;
type RouteValue = (typeof ROUTES)[RouteKey];

interface RouteState {
  view: RouteValue;
  params?: {
    questionId?: string;
    page?: number;
    /** Tracks which view the user navigated from (for back navigation) */
    source?: RouteValue;
  };
}

/**
 * Recover the My Questions origin only from the exact history entry written by
 * this widget. Host-page history state is untrusted and must not redirect the
 * detail back action unless its route and question id agree with the URL.
 */
function readMyQuestionsHistorySource(
  historyState: unknown,
  questionId: string
): typeof ROUTES.MY_QUESTIONS | undefined {
  if (typeof historyState !== 'object' || historyState === null || Array.isArray(historyState)) {
    return undefined;
  }

  const state = historyState as Record<string, unknown>;
  if (
    !Object.prototype.hasOwnProperty.call(state, 'view') ||
    state.view !== ROUTES.QUESTION_DETAIL ||
    !Object.prototype.hasOwnProperty.call(state, 'params') ||
    typeof state.params !== 'object' ||
    state.params === null ||
    Array.isArray(state.params)
  ) {
    return undefined;
  }

  const params = state.params as Record<string, unknown>;
  return Object.prototype.hasOwnProperty.call(params, 'questionId') &&
    Object.prototype.hasOwnProperty.call(params, 'source') &&
    params.questionId === questionId &&
    params.source === ROUTES.MY_QUESTIONS
    ? ROUTES.MY_QUESTIONS
    : undefined;
}

/**
 * Check if running in development mode.
 * Exported as a function to enable mocking in tests.
 *
 * Safe to call in SSR/non-browser contexts (returns false).
 *
 * @returns true if running on localhost or 127.0.0.1
 *
 * @example
 * // Tests can mock window.location.hostname to test production behavior:
 * mockLocation({ hostname: 'example.mybigcommerce.com' });
 * expect(isDevelopment()).toBe(false);
 */
export function isDevelopment(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  );
}

// =============================================================================
// ERROR BOUNDARY
// =============================================================================

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component to catch and handle React errors gracefully.
 * Prevents widget crashes from affecting the host storefront page.
 *
 * @see https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */
class WidgetErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error(`Widget error caught by boundary: ${error.message}`);
    logger.debug(`Error stack: ${errorInfo.componentStack}`);
  }

  handleReset = (): void => {
    // ADR-028 Slice 3: discard retained public reads before remounting.
    //
    // A complete, transport-valid response can still encounter an unexpected
    // renderer or platform fault. Without this invalidation, "Try again" could
    // seed the same admitted payload and repeat the failure without issuing a
    // new request. Recovery must mean "throw away what you had and go get it
    // again"; the browser boundary contract verifies that second request.
    invalidatePublicReads();
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="fmp-error" role="alert">
          <h2 className="fmp-error__title">Something went wrong</h2>
          <p className="fmp-error__message">
            We encountered an error loading the Find My Part widget.
          </p>
          <button type="button" className="fmp-error__button" onClick={this.handleReset}>
            Try again
          </button>
          <style>{errorStyles}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

const errorStyles = `
  :where(.fmp-widget-scope) .fmp-error {
    padding: var(--fmp-space-2xl);
    text-align: center;
    border: var(--fmp-border-width) solid var(--fmp-color-error-border);
    border-radius: var(--fmp-radius-surface);
    background-color: var(--fmp-color-error-bg);
    max-width: var(--fmp-size-400);
    margin: var(--fmp-space-2xl) auto;
  }

  :where(.fmp-widget-scope) .fmp-error__title {
    margin: 0 0 var(--fmp-space-sm) 0;
    font-size: var(--fmp-font-size-lg);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-error-dark);
  }

  :where(.fmp-widget-scope) .fmp-error__message {
    margin: 0 0 var(--fmp-space-lg) 0;
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-error-dark);
  }

  :where(.fmp-widget-scope) .fmp-error__button {
    padding: var(--fmp-space-sm) var(--fmp-space-lg);
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text-on-primary);
    background-color: var(--fmp-color-error);
    border: none;
    border-radius: var(--fmp-radius-control);
    cursor: pointer;
    transition: background-color var(--fmp-duration-normal) var(--fmp-ease-standard);
  }

  :where(.fmp-widget-scope) .fmp-error__button:hover {
    background-color: var(--fmp-color-error-dark);
  }

  :where(.fmp-widget-scope) .fmp-error__button:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-error);
    outline-offset: var(--fmp-outline-offset);
  }
`;

// =============================================================================
// ROUTING HELPERS
// =============================================================================

/**
 * Routing Strategy:
 *
 * Two strategies are supported:
 *
 *   `'hash'` — uses `window.location.hash` (`#browse`, `#my-questions`,
 *              `#question?questionId=…`). Pathname is never modified, so
 *              the widget can be embedded inside a single BC Page (e.g.
 *              MSPL Mode 2 publishing) without triggering BC storefront
 *              404s for paths that have no backing BC Page row.
 *   `'path'` — uses History API (`pushState` to `/find-my-part/browse`,
 *              `/find-my-part/my-questions`, etc.). Requires backing BC
 *              Page rows to exist for every nav target — otherwise the
 *              storefront returns a 404 chrome around the widget on
 *              refresh / direct-link, AND the URL bar update causes some
 *              themes to re-render the BC 404 template even on
 *              same-document navigation.
 *
 * Resolution order (checked once at module init / first nav):
 *   1. `isDevelopment()` (localhost / 127.0.0.1) → `'hash'` always.
 *      Local dev never has BC Page backing rows.
 *   2. `data-routing-strategy="path|hash"` on the one mount accepted by
 *      the total SurfaceMountV1 resolver. The
 *      MSPL renderer (`PublicRendererService.buildBody`) emits
 *      `data-routing-strategy="hash"` on `MSPL_MOUNT_ID` by default
 *      because Mode 2 ships ONE BC Page per question; the widget's
 *      Browse/Ask/My-Questions tabs don't have backing BC Pages.
 *      Phase 3B can flip this to `"path"` when backing pages exist.
 *   3. Default: `'hash'` — safer in embedded contexts.
 *
 * The 2026-05-08 OGS sandbox `pageType=page` control run hit the
 * BC-404-around-the-widget symptom because production was defaulting
 * to `'path'` regardless of context. Hash is the safe default.
 */

/**
 * Resolves the routing strategy from the current DOM/environment.
 *
 * Pure-ish: takes a `Document` so tests can pass a jsdom fixture or
 * exercise the helper without needing to mount the full widget.
 */
export function getRoutingStrategy(
  doc: Document = document,
  pathname: string = pathnameForDocument(doc),
  attestedMount?: ResolvedMountPoint | null
): 'hash' | 'path' {
  if (isDevelopment() && attestedMount === undefined) return 'hash';
  return mountForDecisionReads(doc, pathname, attestedMount)?.routingStrategy ?? 'hash';
}

/**
 * Reads the question id the MSPL detail-page renderer stamps onto the
 * widget mount div via `data-question-id`.
 *
 * Canonical Q&A detail pages at `/find-my-part/questions/<slug>/` ship ONE
 * BC Page whose body contains
 * `<div id="fmp-widget-mount" data-fmp-surface="question-actions"
 * data-fmp-contract-version="1" data-question-id="q_…" data-routing-strategy="hash">`
 * (emitted by `worker/PublicRendererService.buildBody`). The hub mount and
 * off-index Grade-1 fallback omit question identity; the crawlable browse
 * index emits no mount at all. UXR-006A retains the id as attested context but
 * leaves a valid `question-actions` contract static-only; Slice 2B owns the
 * controls island that will consume it. On pre-contract compatibility markup,
 * presence of a question id continues to select the legacy
 * `QuestionDetailView`/`ReplyForm` path. The canonical URL carries the question
 * slug, while either consumer receives identity only from this mount attribute.
 *
 * Consumes the bootstrap's retained mount attestation in production, so DOM
 * replacement cannot make routing, identity and the already-created React root
 * select different candidates mid-session. Returns the trimmed id, or `null`
 * when the attested mount carries no question id.
 *
 * Pure-ish: takes a `Document` so tests can pass a jsdom fixture.
 */
export function getMountQuestionId(
  doc: Document = document,
  pathname: string = pathnameForDocument(doc),
  attestedMount?: ResolvedMountPoint | null
): string | null {
  return mountForDecisionReads(doc, pathname, attestedMount)?.questionId ?? null;
}

function mountForDecisionReads(
  doc: Document,
  pathname: string,
  explicitlyAttested?: ResolvedMountPoint | null
): ResolvedMountPoint | null {
  if (explicitlyAttested !== undefined) return explicitlyAttested;
  if (
    typeof document !== 'undefined' &&
    doc === document &&
    productionMountAttestation !== undefined
  ) {
    return productionMountAttestation;
  }
  return resolveSurfaceMountV1(doc, { pathname, createFallback: false }).mount;
}

function pathnameForDocument(doc: Document): string {
  if (doc.defaultView?.location) return doc.defaultView.location.pathname;
  if (typeof window !== 'undefined' && doc === window.document) return window.location.pathname;
  return PAGE_PATH;
}

function normalizeBrowsePage(value: unknown): number {
  const page = typeof value === 'number' ? value : Number(value ?? 1);
  return Number.isSafeInteger(page) && page >= 1 ? page : 1;
}

/**
 * Get the current view from the URL path.
 * In development mode, uses hash-based routing for easier testing.
 * In production mode, uses path-based routing.
 *
 * @returns The current route state or null if no match
 */
function getCurrentView(
  defaultView: RouteValue = ROUTES.ASK,
  historyState: unknown = window.history.state
): RouteState | null {
  const strategy = getRoutingStrategy();
  if (strategy === 'hash') {
    // Hash-based routing (e.g., #ask, #my-questions, #question?questionId=q_abc).
    // Used in development (localhost) AND in embedded production mode by
    // default — see "Routing Strategy" comment above.
    const hash = window.location.hash.slice(1); // Remove the # prefix
    const [route, queryString] = hash.split('?');
    const params = new URLSearchParams(queryString || '');

    if (route === ROUTES.QUESTION_DETAIL) {
      const questionId = params.get('questionId');
      if (questionId) {
        const source =
          params.get('source') === ROUTES.MY_QUESTIONS ? ROUTES.MY_QUESTIONS : undefined;
        return {
          view: ROUTES.QUESTION_DETAIL,
          params: { questionId, ...(source ? { source } : {}) },
        };
      }
    }

    if (route === ROUTES.BROWSE) {
      const page = normalizeBrowsePage(params.get('page'));
      return { view: ROUTES.BROWSE, params: { page } };
    }

    if (route === ROUTES.ASK || route === ROUTES.MY_QUESTIONS) {
      return { view: route as RouteValue };
    }

    // No hash route matched. On a canonical MSPL detail page
    // (`/find-my-part/questions/<slug>/`) the renderer stamps the question
    // id on the mount div; hydrate the detail view — which embeds the public
    // ReplyForm — so a visitor who lands on the canonical Q&A URL can read
    // and reply without an operator workaround. The hub / Browse / Ask index
    // pages omit the attribute, so they still fall through to ASK.
    const detailQuestionId = getMountQuestionId();
    if (detailQuestionId) {
      return { view: ROUTES.QUESTION_DETAIL, params: { questionId: detailQuestionId } };
    }

    // The attested Hub defaults to discovery; legacy/direct consumers retain
    // the historical Ask default unless their caller opts into Hub behavior.
    return { view: defaultView };
  }

  // Path-based routing (`strategy === 'path'`). Requires backing BC Page
  // rows for every nav target — see the strategy comment above.
  const path = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);

  // Match /find-my-part/question/:questionId
  const questionMatch = path.match(/^\/find-my-part\/question\/([^/]+)\/?$/);
  if (questionMatch) {
    const questionId = questionMatch[1];
    const source = readMyQuestionsHistorySource(historyState, questionId);
    return {
      view: ROUTES.QUESTION_DETAIL,
      params: { questionId, ...(source ? { source } : {}) },
    };
  }

  // Match /find-my-part/browse
  if (path === `${PAGE_PATH}${ROUTES.BROWSE}` || path === `${PAGE_PATH}${ROUTES.BROWSE}/`) {
    const page = normalizeBrowsePage(searchParams.get('page'));
    return { view: ROUTES.BROWSE, params: { page } };
  }

  // Check for specific routes
  if (path === `${PAGE_PATH}${ROUTES.ASK}` || path === `${PAGE_PATH}${ROUTES.ASK}/`) {
    return { view: ROUTES.ASK };
  }
  if (
    path === `${PAGE_PATH}${ROUTES.MY_QUESTIONS}` ||
    path === `${PAGE_PATH}${ROUTES.MY_QUESTIONS}/`
  ) {
    return { view: ROUTES.MY_QUESTIONS };
  }

  // Handle base path without trailing slash
  if (path === '/find-my-part') {
    return { view: defaultView };
  }

  // Default for the base path or unknown paths within /find-my-part/.
  if (path.startsWith(PAGE_PATH)) {
    // Same canonical-detail-page hydration as the hash branch: when the
    // renderer stamped a question id on the mount div, prefer the detail
    // view. The `/find-my-part/question/:id` SPA route above already covers
    // internal path-strategy navigation; this covers the canonical
    // `/find-my-part/questions/<slug>/` BC Page, where only the mount
    // attribute carries the id.
    const detailQuestionId = getMountQuestionId();
    if (detailQuestionId) {
      return { view: ROUTES.QUESTION_DETAIL, params: { questionId: detailQuestionId } };
    }
    return { view: defaultView };
  }

  return null;
}

/**
 * Navigate to a specific view.
 * In development mode, updates the hash (triggers hashchange event).
 * In production mode, uses History API for SPA-like navigation without page reload.
 *
 * Note: This function is exported for testing purposes. Internal navigation
 * should go through the handleNavigate callback in FindMyPartWidget.
 *
 * @param view - The view to navigate to
 * @param params - Optional route parameters
 */
function navigateTo(view: RouteValue, params?: Record<string, string | number>): void {
  const strategy = getRoutingStrategy();
  if (strategy === 'hash') {
    let hash: string = view;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        searchParams.set(key, String(value));
      });
      const qs = searchParams.toString();
      if (qs) {
        hash = `${view}?${qs}`;
      }
    }
    window.location.hash = hash;
  } else {
    // Use History API for SPA-like navigation (no page reload)
    let newPath = `${PAGE_PATH}${view}`;
    if (view === ROUTES.QUESTION_DETAIL && params?.questionId) {
      newPath = `${PAGE_PATH}question/${params.questionId}`;
    }
    if (params?.page && Number(params.page) > 1) {
      newPath += `?page=${params.page}`;
    }
    window.history.pushState({ view, params }, '', newPath);
    // Dispatch popstate to trigger the event listener and update React state
    window.dispatchEvent(new PopStateEvent('popstate', { state: { view, params } }));
  }
}

// =============================================================================
// NAVIGATION COMPONENT
// =============================================================================

interface NavigationProps {
  currentView: RouteValue;
  onNavigate: (view: RouteValue) => void;
}

/**
 * Hub hierarchy: discovery is home, Ask is the primary action, and My
 * Questions is a utility rather than a co-equal tab.
 */
function Navigation({ currentView, onNavigate }: NavigationProps): React.ReactElement {
  // A direct or Browse-origin detail keeps discovery active. The parent passes
  // My Questions for a detail entry that originated from that utility view.
  const activeView = currentView === ROUTES.QUESTION_DETAIL ? ROUTES.BROWSE : currentView;

  return (
    <nav className="fmp-nav" aria-label="Find My Part navigation">
      <button
        type="button"
        className={`fmp-nav__home ${activeView === ROUTES.BROWSE ? 'fmp-nav__home--active' : ''}`}
        onClick={() => onNavigate(ROUTES.BROWSE)}
        aria-current={activeView === ROUTES.BROWSE ? 'page' : undefined}
      >
        Find a part
      </button>
      <span className="fmp-nav__actions">
        <button
          type="button"
          className={`fmp-nav__ask ${activeView === ROUTES.ASK ? 'fmp-nav__ask--active' : ''}`}
          onClick={() => onNavigate(ROUTES.ASK)}
          aria-current={activeView === ROUTES.ASK ? 'page' : undefined}
        >
          Ask with photos
        </button>
        <button
          type="button"
          className={`fmp-nav__utility ${activeView === ROUTES.MY_QUESTIONS ? 'fmp-nav__utility--active' : ''}`}
          onClick={() => onNavigate(ROUTES.MY_QUESTIONS)}
          aria-current={activeView === ROUTES.MY_QUESTIONS ? 'page' : undefined}
        >
          My Questions
        </button>
      </span>
    </nav>
  );
}

// =============================================================================
// SKIP LINK COMPONENT
// =============================================================================

/**
 * Skip link for keyboard and screen reader users.
 * Allows users to bypass navigation and jump directly to main content.
 * Visually hidden until focused.
 *
 * Respects user motion preferences per WCAG 2.3.3 (Animation from Interactions).
 *
 * @see WCAG 2.1 Success Criterion 2.4.1: Bypass Blocks (Level A)
 */
function SkipLink({ targetId }: { targetId: string }): React.ReactElement {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.focus();
      // Respect prefers-reduced-motion for users with vestibular disorders (WCAG 2.3.3)
      target.scrollIntoView({ behavior: motionSafeScrollBehavior() });
    }
  };

  return (
    <a href={`#${targetId}`} className="fmp-skip-link" onClick={handleClick}>
      Skip to Find My Part content
    </a>
  );
}

// =============================================================================
// MAIN WIDGET COMPONENT
// =============================================================================

/**
 * Main widget component with routing and navigation.
 * Renders the appropriate view based on the current route.
 */
export interface FindMyPartWidgetProps {
  readonly hubExperience?: boolean;
}

function FindMyPartWidget({ hubExperience = false }: FindMyPartWidgetProps): React.ReactElement {
  const instanceIdRef = useRef<number | null>(null);
  if (instanceIdRef.current === null) {
    widgetInstanceSequence += 1;
    instanceIdRef.current = widgetInstanceSequence;
  }
  const defaultView = hubExperience ? ROUTES.BROWSE : ROUTES.ASK;
  const [routeState, setRouteState] = useState<RouteState>(() => {
    return getCurrentView(defaultView) || { view: defaultView };
  });
  const routeView = routeState.view;
  const routePage = normalizeBrowsePage(routeState.params?.page);
  const routeQuestionId = routeState.params?.questionId;
  const routeSource = routeState.params?.source;

  // The host document owns its main landmark. This named Widget region owns
  // route-transition focus so embedded pages never acquire a nested main.
  const contentRegionRef = useRef<HTMLElement>(null);
  const previousRouteRef = useRef({
    view: routeView,
    page: routePage,
    questionId: routeQuestionId,
    source: routeSource,
  });
  const { announce, clear, Announcer } = useAnnounce();

  // Stable ID for the skip-link target and focusable route region.
  const mainContentId = `fmp-widget-current-view-${instanceIdRef.current}`;

  const viewName =
    routeView === ROUTES.ASK
      ? 'Ask a Question'
      : routeView === ROUTES.BROWSE
        ? 'Browse Questions'
        : routeView === ROUTES.MY_QUESTIONS
          ? 'My Questions'
          : 'Question Detail';
  const currentViewLabel = `${viewName} view${
    routeView === ROUTES.BROWSE ? `, page ${routePage}` : ''
  }`;

  /**
   * Route changes use one explicit channel. The named current-view region
   * receives focus when focus is elsewhere. A persistent polite live region
   * announces the destination only when that same region is already focused
   * (the normal browser Back/Forward case, where calling focus again emits no
   * event). Primitive route identity makes initial StrictMode effect replay and
   * an action's subsequent URL echo observable no-ops.
   */
  useEffect(() => {
    const previous = previousRouteRef.current;
    const next = {
      view: routeView,
      page: routePage,
      questionId: routeQuestionId,
      source: routeSource,
    };
    if (
      previous.view === next.view &&
      previous.page === next.page &&
      previous.questionId === next.questionId &&
      previous.source === next.source
    ) {
      return;
    }
    previousRouteRef.current = next;

    const isBrowsePageTransition =
      previous.view === ROUTES.BROWSE && routeView === ROUTES.BROWSE && previous.page !== routePage;
    const transitionTimer = window.setTimeout(() => {
      const region = contentRegionRef.current;
      if (!region) return;
      if (isBrowsePageTransition) {
        region.scrollIntoView({ behavior: motionSafeScrollBehavior() });
      }
      if (document.activeElement === region) {
        announce(`Navigated to ${currentViewLabel}.`);
      } else {
        // Focus is the sole destination signal on this path. Remove any prior
        // history announcement so stale route text cannot outlive the view.
        clear('polite');
        region.focus(isBrowsePageTransition ? { preventScroll: true } : undefined);
      }
    }, 0);
    return () => window.clearTimeout(transitionTimer);
  }, [announce, clear, currentViewLabel, routePage, routeQuestionId, routeSource, routeView]);

  /**
   * Handle browser back/forward navigation.
   */
  useEffect(() => {
    const handlePopState = (event: PopStateEvent | HashChangeEvent): void => {
      const historyState = event instanceof PopStateEvent ? event.state : window.history.state;
      const state = getCurrentView(defaultView, historyState);
      if (state) {
        setRouteState(state);
      }
    };

    // Capture development mode at effect creation time to ensure cleanup
    // removes the same listener that was registered (prevents test pollution
    // if location is mocked between mount and unmount)
    const inDevelopment = isDevelopment();

    // Listen for hash changes in development mode
    if (inDevelopment) {
      window.addEventListener('hashchange', handlePopState);
    } else {
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      if (inDevelopment) {
        window.removeEventListener('hashchange', handlePopState);
      } else {
        window.removeEventListener('popstate', handlePopState);
      }
    };
  }, [defaultView]);

  /**
   * Handle navigation to a different view.
   * Updates React state and browser URL. The route-focus effect owns the sole
   * route-change announcement channel.
   */
  const handleNavigate = useCallback((view: RouteValue) => {
    setRouteState({ view });
    navigateTo(view);
  }, []);

  /**
   * Handle "Ask a Question" CTA from browse empty state (GAP-005).
   */
  const handleAskQuestion = useCallback(() => {
    handleNavigate(ROUTES.ASK);
  }, [handleNavigate]);

  const handleBrowseQuestions = useCallback(() => {
    handleNavigate(ROUTES.BROWSE);
  }, [handleNavigate]);

  /**
   * Handle successful question submission.
   */
  const handleQuestionSuccess = useCallback((questionId: string, _slug: string) => {
    logger.info(`Question submitted successfully: ${questionId}`);
  }, []);

  /**
   * Handle question click in My Questions list.
   */
  const handleMyQuestionClick = useCallback((questionId: string) => {
    setRouteState({
      view: ROUTES.QUESTION_DETAIL,
      params: { questionId, source: ROUTES.MY_QUESTIONS },
    });
    navigateTo(ROUTES.QUESTION_DETAIL, { questionId, source: ROUTES.MY_QUESTIONS });
  }, []);

  /**
   * Handle back navigation from question detail (GAP-005).
   * Returns to the view the user came from (Browse or My Questions).
   */
  const handleBackFromDetail = useCallback(() => {
    const source = routeState.params?.source;

    if (source === ROUTES.MY_QUESTIONS) {
      setRouteState({ view: ROUTES.MY_QUESTIONS });
      navigateTo(ROUTES.MY_QUESTIONS);
    } else {
      // Canonical Browse rows now leave this SPA through real anchors, so no
      // current writer owns a saved-page key. Legacy/direct detail surfaces
      // therefore return deterministically to page 1 and ignore stale session
      // data left by an older widget bundle.
      setRouteState({ view: ROUTES.BROWSE, params: { page: 1 } });
      navigateTo(ROUTES.BROWSE);
    }
  }, [routeState.params?.source]);

  /**
   * Handle page change in browse view (GAP-005).
   */
  const handlePageChange = useCallback((page: number) => {
    setRouteState({ view: ROUTES.BROWSE, params: { page } });
    navigateTo(ROUTES.BROWSE, page > 1 ? { page } : undefined);
  }, []);

  const widget = (
    <div
      className={`fmp-widget ${routeState.view === ROUTES.ASK ? 'fmp-widget--ask' : ''} ${routeState.view === ROUTES.BROWSE ? 'fmp-widget--browse' : ''}`}
    >
      <SkipLink targetId={mainContentId} />
      <Announcer />
      {!hubExperience && (
        <header className="fmp-widget__header">
          <h2 className="fmp-widget__title">Find My Part</h2>
          <p className="fmp-widget__tagline">
            Get help identifying the right part for your project
          </p>
        </header>
      )}
      <Navigation
        currentView={
          routeState.view === ROUTES.QUESTION_DETAIL &&
          routeState.params?.source === ROUTES.MY_QUESTIONS
            ? ROUTES.MY_QUESTIONS
            : routeState.view
        }
        onNavigate={handleNavigate}
      />

      <section
        id={mainContentId}
        ref={contentRegionRef}
        className="fmp-widget__content"
        tabIndex={-1}
        aria-label={currentViewLabel}
      >
        {routeState.view === ROUTES.ASK && (
          <QuestionForm
            onSuccess={handleQuestionSuccess}
            onError={(error) => logger.error(`Question submission failed: ${error.message}`)}
          />
        )}
        {routeState.view === ROUTES.BROWSE && (
          <BrowseQuestionsView
            onAskQuestion={handleAskQuestion}
            page={routePage}
            onPageChange={handlePageChange}
          />
        )}
        {routeState.view === ROUTES.MY_QUESTIONS && (
          <MyQuestions
            onQuestionClick={handleMyQuestionClick}
            onBrowseQuestions={handleBrowseQuestions}
            onAskQuestion={handleAskQuestion}
          />
        )}
        {routeState.view === ROUTES.QUESTION_DETAIL && routeState.params?.questionId && (
          <QuestionDetailView
            questionId={routeState.params.questionId}
            onBack={handleBackFromDetail}
          />
        )}
      </section>

      {isDevelopment() && (
        <div className="fmp-widget__dev-notice">
          <p>Development mode — Current view: {routeState.view}</p>
        </div>
      )}

      <style>{widgetStyles}</style>
    </div>
  );

  return <ViewerProvider>{widget}</ViewerProvider>;
}

// =============================================================================
// PUBLIC AVAILABILITY GATE (Phase 4.5 controlled-launch slice)
// =============================================================================

type AvailabilityState = 'loading' | 'available' | 'unavailable';
export type AvailabilityUnavailableBehavior = 'panel' | 'nothing';

interface AvailabilityGateProps {
  readonly unavailableBehavior?: AvailabilityUnavailableBehavior;
  readonly onUnavailable?: () => void;
  readonly hubExperience?: boolean;
}

/**
 * Gates legacy SPA and contract-hub roots behind the tenant's resolved public
 * launch mode. Renders the normal widget ONLY when availability resolves to
 * VISIBLE. Legacy/fallback roots retain the controlled unavailable panel;
 * canonical contract hubs render nothing for every non-VISIBLE outcome.
 * Canonical `question-actions` pages deliberately never enter this legacy gate:
 * UXR-006A leaves them renderer-only until Slice 2B supplies controls.
 *
 * FAIL CLOSED: any non-success response, a non-VISIBLE mode, or a thrown error
 * suppresses the form. The caller selects the surface-owned unavailable output.
 *
 * Development mode (localhost) bypasses the gate so the local dev harness and
 * component tests are unaffected; this is a production-surface control.
 */
function AvailabilityGateInner({
  unavailableBehavior = 'panel',
  onUnavailable,
  hubExperience = false,
}: AvailabilityGateProps): React.ReactElement | null {
  const [state, setState] = useState<AvailabilityState>('loading');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    widgetApi.availability
      .get()
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data.launchMode === 'VISIBLE') {
          setState('available');
          return;
        }
        // Fail closed: not VISIBLE (or an error envelope) → suppress.
        setMessage(res.success ? res.data.unavailableMessage : null);
        onUnavailable?.();
        setState('unavailable');
      })
      .catch(() => {
        if (cancelled) return;
        // Fail closed on any unexpected error.
        setMessage(null);
        onUnavailable?.();
        setState('unavailable');
      });

    return () => {
      cancelled = true;
    };
  }, [onUnavailable]);

  if (state === 'loading') {
    // Render nothing while resolving — avoids flashing either the form or the
    // unavailable panel before the tenant state is known.
    return null;
  }

  if (state === 'unavailable') {
    return unavailableBehavior === 'nothing' ? null : <LaunchUnavailable message={message} />;
  }

  return <FindMyPartWidget hubExperience={hubExperience} />;
}

/**
 * Top-level gate rendered by bootstrap for development, legacy SPA mounts and
 * contract hubs. Dev bypass keeps local development and existing component
 * tests (which render `<FindMyPartWidget />` directly) unaffected.
 */
export function AvailabilityGate({
  unavailableBehavior = 'panel',
  onUnavailable,
  hubExperience = false,
}: AvailabilityGateProps = {}): React.ReactElement {
  if (isDevelopment()) {
    return <FindMyPartWidget hubExperience={hubExperience} />;
  }
  return (
    <AvailabilityGateInner
      unavailableBehavior={unavailableBehavior}
      onUnavailable={onUnavailable}
      hubExperience={hubExperience}
    />
  );
}

// =============================================================================
// STYLES
// =============================================================================

const widgetStyles = `
  :where(.fmp-widget-scope) .fmp-widget {
    inline-size: 100%;
    max-inline-size: var(--fmp-layout-widget-max);
    margin-inline: auto;
    font-family: var(--fmp-font-family);
    position: relative;
    container-name: fmp-widget;
    container-type: inline-size;
  }

  :where(.fmp-widget-scope.fmp-widget-scope--hub-layout-v1) > .fmp-widget {
    max-inline-size: var(--fmp-layout-view-max);
    margin-inline: 0;
  }

  :where(.fmp-widget-scope) .fmp-widget--ask {
    container-name: fmp-widget fmp-ask;
  }

  :where(.fmp-widget-scope:not(.fmp-widget-scope--hub-layout-v1)) .fmp-widget--ask,
  :where(.fmp-widget-scope:not(.fmp-widget-scope--hub-layout-v1)) .fmp-widget--browse {
    max-inline-size: var(--fmp-layout-view-max);
  }

  /* Widget Header */
  :where(.fmp-widget-scope) .fmp-widget__header {
    margin-bottom: var(--fmp-space-lg);
  }

  :where(.fmp-widget-scope) .fmp-widget__title {
    margin: 0 0 var(--fmp-space-xs);
    font-size: var(--fmp-font-size-xl);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text);
    line-height: var(--fmp-line-height-heading);
  }

  :where(.fmp-widget-scope) .fmp-widget__tagline {
    margin: 0;
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-text-muted);
    line-height: var(--fmp-line-height-body);
  }

  /* Skip Link - Visually hidden until focused (WCAG 2.4.1 Bypass Blocks) */
  :where(.fmp-widget-scope) .fmp-skip-link {
    position: relative;
    display: block;
    width: fit-content;
    height: 0;
    z-index: var(--fmp-z-skip-link);
    padding: 0;
    overflow: hidden;
    background-color: var(--fmp-color-primary);
    color: var(--fmp-color-text-on-primary);
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    text-decoration: none;
    border-radius: 0 0 var(--fmp-radius-control) 0;
    opacity: 0;
    transition: opacity var(--fmp-duration-normal) var(--fmp-ease-standard);
  }

  :where(.fmp-widget-scope) .fmp-skip-link:focus-visible {
    height: auto;
    padding: var(--fmp-space-md) var(--fmp-space-2xl);
    opacity: 1;
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-text-on-primary);
    outline-offset: var(--fmp-outline-inset-offset);
  }

  :where(.fmp-widget-scope) .fmp-skip-link:hover {
    background-color: var(--fmp-color-primary-hover);
  }

  :where(.fmp-widget-scope) .fmp-nav {
    display: flex;
    align-items: stretch;
    flex-direction: column;
    gap: var(--fmp-space-lg);
    margin-bottom: var(--fmp-space-2xl);
    border-bottom: var(--fmp-border-width) solid var(--fmp-color-border);
    padding-bottom: var(--fmp-space-sm);
  }

  :where(.fmp-widget-scope) .fmp-nav button {
    min-height: var(--fmp-size-44);
    border-radius: var(--fmp-radius-control);
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    font-family: inherit;
    cursor: pointer;
  }

  :where(.fmp-widget-scope) .fmp-nav__home,
  :where(.fmp-widget-scope) .fmp-nav__utility {
    padding: var(--fmp-space-sm) var(--fmp-space-md);
    border: none;
    background: transparent;
    color: var(--fmp-color-text-muted);
  }

  :where(.fmp-widget-scope) .fmp-nav__home:hover,
  :where(.fmp-widget-scope) .fmp-nav__utility:hover,
  :where(.fmp-widget-scope) .fmp-nav__home--active,
  :where(.fmp-widget-scope) .fmp-nav__utility--active {
    color: var(--fmp-color-primary);
    background: var(--fmp-color-primary-light);
  }

  :where(.fmp-widget-scope) .fmp-nav__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    gap: var(--fmp-space-sm);
  }

  :where(.fmp-widget-scope) .fmp-nav__ask {
    padding: var(--fmp-space-sm) var(--fmp-space-lg);
    border: var(--fmp-border-width) solid var(--fmp-color-primary);
    background: var(--fmp-color-primary);
    color: var(--fmp-color-text-on-primary);
  }

  :where(.fmp-widget-scope) .fmp-nav__ask:hover,
  :where(.fmp-widget-scope) .fmp-nav__ask--active {
    border-color: var(--fmp-color-primary-hover);
    background: var(--fmp-color-primary-hover);
  }

  :where(.fmp-widget-scope) .fmp-nav button[aria-current="page"] {
    text-decoration: underline;
  }

  :where(.fmp-widget-scope) .fmp-nav button:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-primary);
    outline-offset: var(--fmp-outline-offset);
  }

  :where(.fmp-widget-scope) .fmp-nav__ask:focus-visible {
    outline-color: var(--fmp-color-text-on-primary);
    outline-offset: var(--fmp-outline-inset-offset);
  }

  @container fmp-widget (min-width: ${BREAKPOINTS.container.compact}) {
    :where(.fmp-widget-scope) .fmp-nav {
      align-items: center;
      flex-direction: row;
      justify-content: space-between;
    }

    :where(.fmp-widget-scope) .fmp-nav__actions {
      display: flex;
    }
  }

  :where(.fmp-widget-scope) .fmp-widget__content {
    inline-size: 100%;
    min-height: var(--fmp-size-200);
    padding-inline: var(--fmp-space-lg);
  }

  /* Show focus outline when focused via keyboard navigation. Programmatic
     focus retains the user agent's visible treatment instead of suppressing it. */
  :where(.fmp-widget-scope) .fmp-widget__content:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-primary);
    outline-offset: var(--fmp-outline-offset);
  }

  @container fmp-widget (min-width: ${BREAKPOINTS.container.compact}) {
    :where(.fmp-widget-scope) .fmp-widget__content {
      padding-inline: var(--fmp-space-xl);
    }
  }

  @container fmp-widget (min-width: ${BREAKPOINTS.container.wide}) {
    :where(.fmp-widget-scope) .fmp-widget__content {
      padding-inline: var(--fmp-space-section);
    }
  }

  :where(.fmp-widget-scope) .fmp-widget__dev-notice {
    margin-top: var(--fmp-space-2xl);
    padding: var(--fmp-space-md) var(--fmp-space-lg);
    background-color: var(--fmp-color-warning-bg);
    border-radius: var(--fmp-radius-control);
    font-size: var(--fmp-font-size-sm);
    color: var(--fmp-color-warning-text);
  }

  :where(.fmp-widget-scope) .fmp-widget__dev-notice p {
    margin: 0;
  }

  /* High contrast mode support */
  @media (prefers-contrast: high) {
    :where(.fmp-widget-scope) .fmp-nav button:focus-visible {
      outline-color: currentColor;
    }

    :where(.fmp-widget-scope) .fmp-nav__home--active,
    :where(.fmp-widget-scope) .fmp-nav__utility--active {
      border: var(--fmp-border-width) solid currentColor;
    }
  }
`;

// =============================================================================
// BOOTSTRAP
// =============================================================================

/**
 * Module-level root reference for HMR cleanup.
 * Stores the React root to reuse during hot module reloads, preventing
 * multiple roots from being created which would cause memory leaks and
 * React warnings about calling createRoot() on a container that already
 * has a root.
 *
 * @see Task 0C-26: HMR root reference tracking
 */
let widgetRoot: Root | null = null;
let widgetSchemeCleanup: (() => void) | null = null;
let widgetDomReadyCleanup: (() => void) | null = null;
/** Immutable decision snapshot retained for the lifetime of one production boot. */
let productionMountAttestation: ResolvedMountPoint | null | undefined;

const WIDGET_TOKEN_STYLE_ATTRIBUTE = 'data-fmp-widget-token-contract';
const WIDGET_TOKEN_STYLE_VERSION = 'uxr-006a-v1';

/**
 * Install the carrier-scoped token stylesheet before React can render.
 * The style lives in the document head so React cannot remove it while
 * replacing loading, launch-unavailable, normal, or error-boundary children.
 */
export function installWidgetTokenStyles(doc: Document): HTMLStyleElement {
  const existing = doc.head.querySelector<HTMLStyleElement>(
    `style[${WIDGET_TOKEN_STYLE_ATTRIBUTE}]`
  );
  if (existing) {
    existing.setAttribute(WIDGET_TOKEN_STYLE_ATTRIBUTE, WIDGET_TOKEN_STYLE_VERSION);
    if (existing.textContent !== WIDGET_TOKEN_CSS) existing.textContent = WIDGET_TOKEN_CSS;
    return existing;
  }

  const style = doc.createElement('style');
  style.setAttribute(WIDGET_TOKEN_STYLE_ATTRIBUTE, WIDGET_TOKEN_STYLE_VERSION);
  style.textContent = WIDGET_TOKEN_CSS;
  doc.head.appendChild(style);
  return style;
}

export {
  findOrCreateProductionMountPoint,
  resolveSurfaceMountV1,
  type ResolvedMountPoint,
} from './mountContract';

/**
 * Bootstrap function - mounts the widget.
 * In development mode: Always mounts to the dev container.
 * In production mode: Only mounts on /find-my-part pages (with or without trailing slash).
 *
 * HMR Support: Reuses the existing root if already created for the same mount point,
 * preventing multiple roots from being created during hot module replacement.
 */
function bootstrap(): void {
  let mountPoint: HTMLElement | null = null;
  let renderTarget: HTMLElement | null = null;
  let mountResolutionContext: HubLayoutV1ResolutionContext | null = null;
  let hubExperience = false;

  if (isDevelopment()) {
    // Development mode - mount to the dev container
    mountPoint = document.getElementById(DEV_MOUNT_ID);
    if (!mountPoint) {
      logger.warn('Dev mount point not found, creating one');
      mountPoint = document.createElement('div');
      mountPoint.id = DEV_MOUNT_ID;
      document.body.appendChild(mountPoint);
    }
    logger.info('Running in development mode');
  } else {
    // Production mode - only initialize on the Find My Part page
    // Handle both /find-my-part and /find-my-part/ (with or without trailing slash)
    const pathname = window.location.pathname;
    const isOnFindMyPartPage = pathname === '/find-my-part' || pathname.startsWith(PAGE_PATH);

    if (!isOnFindMyPartPage) {
      // Could render a floating button elsewhere in the future
      productionMountAttestation = null;
      logger.debug('Not on /find-my-part/ page, widget not mounted');
      return;
    }

    // Resolve one total decision before routing, identity, or React can
    // observe the DOM. The production emitter is deliberately no-op until
    // UXR-006B binds its separately governed transport.
    const resolution = resolveSurfaceMountV1(document, {
      pathname,
      emitter: NOOP_MOUNT_DECISION_EMITTER_V1,
    });
    if (resolution.diagnostic) {
      logger.warn(resolution.diagnostic, {
        candidateCount: resolution.candidateCount,
        pathFamily: resolution.event.pathFamily,
      });
    }
    const resolved = resolution.mount;
    mountResolutionContext = Object.freeze({
      decision: resolution.event.decision,
      pathFamily: resolution.event.pathFamily,
    });
    productionMountAttestation = resolved ? Object.freeze({ ...resolved }) : null;
    if (!productionMountAttestation) {
      if (resolution.event.pathFamily === 'index') {
        logger.debug('INDEX_PATH_MOUNT_EXCLUDED');
        return;
      }
      logger.error('MOUNT_DECISION_NO_MOUNT', null, {
        decision: resolution.event.decision,
        pathFamily: resolution.event.pathFamily,
        candidateCount: resolution.candidateCount,
      });
      return;
    }
    mountPoint = productionMountAttestation.element;
    renderTarget = productionMountAttestation.renderTarget;
    logger.info(
      `Mounting widget at #${productionMountAttestation.mountId} (source: ${productionMountAttestation.source})`
    );
  }

  renderTarget ??= mountPoint;

  // Establish the persistent carrier, safe palette, validated tenant hooks,
  // and stylesheet synchronously before createRoot/render. The mount point is
  // outside every throwable or availability-gated React child.
  installWidgetTokenStyles(document);
  const installedTokens = installWidgetTokenScope(mountPoint);
  const tenantTheme = installedTokens.overrides;
  applyThemeTransaction(mountPoint, installedTokens.scheme, tenantTheme);

  if (productionMountAttestation && mountResolutionContext) {
    const hubLayout = installHubLayoutV1(productionMountAttestation, mountResolutionContext);
    // Surface identity and optional shell styling are separate authorities. An
    // explicit contract Hub remains the Browse-first experience even when its
    // renderer topology drifts and the layout modifier correctly fails closed.
    // The bounded pre-contract topology has no surface attribute, so it still
    // earns Hub identity only through a successful layout recognition.
    hubExperience =
      productionMountAttestation.surface === 'hub' || hubLayout.decision === 'applied';
    if (hubLayout.decision === 'invalid-shell') {
      logger.warn('HUB_LAYOUT_V1_INVALID_SHELL', {
        mountId: productionMountAttestation.mountId,
      });
    }
  }

  // HMR/bootstrap re-entry must never accumulate media-query listeners.
  widgetSchemeCleanup?.();
  widgetSchemeCleanup = subscribeToSchemePreference(installedTokens.hostColorScheme, (scheme) => {
    const currentTheme =
      typeof getComputedStyle === 'function'
        ? readPublicThemeOverrides(mountPoint, getComputedStyle(mountPoint))
        : tenantTheme;
    applyThemeTransaction(mountPoint, scheme, currentTheme);
  });

  // SURFACE_COST_QUESTION_ACTIONS_START
  // Slice 2B: a canonical detail page gets the controls-only interaction
  // island, never the SPA root. The island resolves availability and essential
  // control readiness BEFORE it touches the slot, then atomically replaces the
  // slot's static fallback with one committed live-controls root. Creating the
  // SPA root here (at either the carrier or the slot) would respectively
  // destroy the slot or clear its static child, and would render a second
  // question representation. Legacy detail pages keep the existing SPA path;
  // contract hubs keep the existing hub root.
  if (productionMountAttestation?.surface === 'question-actions') {
    installQuestionActionsIslandV1(productionMountAttestation);
    return;
  }
  // SURFACE_COST_QUESTION_ACTIONS_END

  // Initialize React with ErrorBoundary to prevent widget crashes from affecting host page
  // Reuse existing root for HMR support (prevents multiple roots / memory leaks)
  if (!widgetRoot) {
    widgetRoot = createRoot(renderTarget);
    logger.debug('Created new React root');
  } else {
    logger.debug('Reusing existing React root for HMR');
  }

  // SURFACE_COST_HUB_START
  const unavailableBehavior = productionMountAttestation?.surface === 'hub' ? 'nothing' : 'panel';
  const onUnavailable =
    productionMountAttestation?.surface === 'hub'
      ? (): void => mountPoint.classList.add(SURFACE_MOUNT_V1_HUB_RESERVATION_RELEASED_CLASS)
      : undefined;
  mountPoint.classList.remove(SURFACE_MOUNT_V1_HUB_RESERVATION_RELEASED_CLASS);
  // SURFACE_COST_HUB_END

  widgetRoot.render(
    <React.StrictMode>
      <WidgetErrorBoundary>
        {/* Phase 4.5: gate the public surface behind tenant launch mode. */}
        <AvailabilityGate
          unavailableBehavior={unavailableBehavior}
          onUnavailable={onUnavailable}
          hubExperience={hubExperience}
        />
      </WidgetErrorBoundary>
    </React.StrictMode>
  );

  logger.info('Widget mounted successfully');
}

// Wait for DOM
if (document.readyState === 'loading') {
  const onDomReady = (): void => {
    widgetDomReadyCleanup = null;
    bootstrap();
  };
  document.addEventListener('DOMContentLoaded', onDomReady, { once: true });
  widgetDomReadyCleanup = () => document.removeEventListener('DOMContentLoaded', onDomReady);
} else {
  bootstrap();
}

// =============================================================================
// HMR (HOT MODULE REPLACEMENT) SUPPORT
// =============================================================================

/**
 * Vite HMR handler for proper cleanup during hot module replacement.
 * Clears the root reference so a fresh root is created on next bootstrap.
 *
 * @see https://vitejs.dev/guide/api-hmr.html
 * @see Task 0C-26: HMR root reference tracking
 */
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    widgetDomReadyCleanup?.();
    widgetDomReadyCleanup = null;
    // Clear root reference before module is replaced
    // The new module will create a fresh root
    if (widgetRoot) {
      widgetRoot.unmount();
      widgetRoot = null;
      logger.debug('HMR: Unmounted widget root for module replacement');
    }
    widgetSchemeCleanup?.();
    widgetSchemeCleanup = null;
    // The island owns a root, in-flight requests, delegated listeners and
    // restorable static-DOM state of its own.
    resetQuestionActionsIslandV1();
    productionMountAttestation = undefined;
  });
}

// =============================================================================
// EXPORTS
// =============================================================================

/**
 * Public API exports for the storefront widget.
 *
 * Components:
 * - FindMyPartWidget: Main widget component with routing and navigation
 * - Navigation: Tab-style navigation component (exported for testing)
 * - WidgetErrorBoundary: Error boundary for graceful error handling
 * - SkipLink: Accessibility skip link (exported for testing)
 *
 * Environment utilities:
 * - isDevelopment: Check if running on localhost (mockable function for testing)
 *
 * Routing utilities (exported primarily for testing):
 * - ROUTES: Route constants (ASK, MY_QUESTIONS)
 * - getCurrentView: Get current view from URL
 * - navigateTo: Navigate to a specific view
 *
 * HMR utilities (exported for testing):
 * - getWidgetRoot: Get current root reference (for testing HMR behavior)
 * - resetWidgetRoot: Reset root reference (for test isolation)
 *
 * Types:
 * - RouteValue: Union type of valid route values
 * - NavigationProps: Props interface for Navigation component
 */

/**
 * Get the current widget root reference.
 * Exported for testing HMR behavior.
 *
 * @returns The current React root or null if not initialized
 */
export function getWidgetRoot(): Root | null {
  return widgetRoot;
}

/**
 * Reset the widget root reference.
 * Exported for test isolation - allows tests to start with a clean state.
 * In production, root is managed automatically via HMR dispose handler.
 */
export function resetWidgetRoot(): void {
  if (widgetRoot) {
    widgetRoot.unmount();
    widgetRoot = null;
  }
  widgetSchemeCleanup?.();
  widgetSchemeCleanup = null;
  widgetDomReadyCleanup?.();
  widgetDomReadyCleanup = null;
  resetQuestionActionsIslandV1();
  productionMountAttestation = undefined;
}

// isDevelopment is already exported via `export function` declaration above
export {
  FindMyPartWidget,
  Navigation,
  WidgetErrorBoundary,
  SkipLink,
  ROUTES,
  getCurrentView,
  navigateTo,
};
export type { RouteValue, RouteState, NavigationProps };
